/**
 * Cache service for API responses with support for environment and timeframe-based caching
 */

export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

export interface CacheOptions {
  ttl?: number; // Time to live in milliseconds
  maxSize?: number; // Maximum number of entries to cache
}

export class CacheService {
  private cache = new Map<string, CacheEntry<any>>();
  private readonly defaultTtl: number;
  private readonly maxSize: number;

  constructor(options: CacheOptions = {}) {
    this.defaultTtl = options.ttl || 5 * 60 * 1000; // Default 5 minutes
    this.maxSize = options.maxSize || 100; // Default 100 entries
  }

  /**
   * Generate a cache key based on environment, timeframe, and other parameters
   */
  private generateKey(
    environment: string,
    timeframe: string,
    endpoint: string,
    additionalParams?: Record<string, any>,
  ): string {
    const baseKey = `${environment}:${timeframe}:${endpoint}`;

    if (additionalParams) {
      const sortedParams = Object.keys(additionalParams)
        .sort()
        .map((key) => `${key}=${additionalParams[key]}`)
        .join('&');
      return `${baseKey}:${sortedParams}`;
    }

    return baseKey;
  }

  /**
   * Get cached data if it exists and hasn't expired
   */
  get<T>(
    environment: string,
    timeframe: string,
    endpoint: string,
    additionalParams?: Record<string, any>,
  ): T | null {
    const key = this.generateKey(environment, timeframe, endpoint, additionalParams);
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    // Check if entry has expired
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    console.log(`Cache HIT for key: ${key}`);
    return entry.data;
  }

  /**
   * Set cached data with optional custom TTL
   */
  set<T>(
    environment: string,
    timeframe: string,
    endpoint: string,
    data: T,
    additionalParams?: Record<string, any>,
    customTtl?: number,
  ): void {
    const key = this.generateKey(environment, timeframe, endpoint, additionalParams);
    const ttl = customTtl || this.defaultTtl;
    const now = Date.now();

    // Implement LRU eviction if cache is full
    if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
      // Remove the oldest entry
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
      }
    }

    const entry: CacheEntry<T> = {
      data,
      timestamp: now,
      expiresAt: now + ttl,
    };

    this.cache.set(key, entry);
    console.log(`Cache SET for key: ${key}, TTL: ${ttl}ms`);
  }

  /**
   * Clear all cached entries for a specific environment
   */
  clearEnvironment(environment: string): void {
    const keysToDelete: string[] = [];

    for (const key of this.cache.keys()) {
      if (key.startsWith(`${environment}:`)) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach((key) => this.cache.delete(key));
    console.log(
      `Cache CLEARED for environment: ${environment}, removed ${keysToDelete.length} entries`,
    );
  }

  /**
   * Clear all cached entries for a specific environment and timeframe
   */
  clearEnvironmentTimeframe(environment: string, timeframe: string): void {
    const keysToDelete: string[] = [];
    const prefix = `${environment}:${timeframe}:`;

    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach((key) => this.cache.delete(key));
    console.log(
      `Cache CLEARED for ${environment}:${timeframe}, removed ${keysToDelete.length} entries`,
    );
  }

  /**
   * Clear specific endpoint cache for all environments and timeframes
   */
  clearEndpoint(endpoint: string): void {
    const keysToDelete: string[] = [];

    for (const key of this.cache.keys()) {
      // Check if key contains the endpoint (format: env:timeframe:endpoint:params)
      const parts = key.split(':');
      if (parts.length >= 3 && parts[2] === endpoint) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach((key) => this.cache.delete(key));
    console.log(`Cache CLEARED for endpoint: ${endpoint}, removed ${keysToDelete.length} entries`);
  }

  /**
   * Clear specific pipeline cache entries
   */
  clearPipeline(pipelineName: string): void {
    const keysToDelete: string[] = [];

    for (const key of this.cache.keys()) {
      if (key.includes(`pipeline_name=${pipelineName}`)) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach((key) => this.cache.delete(key));
    console.log(
      `Cache CLEARED for pipeline: ${pipelineName}, removed ${keysToDelete.length} entries`,
    );
  }

  /**
   * Clear all cached entries
   */
  clearAll(): void {
    const count = this.cache.size;
    this.cache.clear();
    console.log(`Cache CLEARED ALL, removed ${count} entries`);
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    size: number;
    maxSize: number;
    keys: string[];
    defaultTtl: number;
  } {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      keys: Array.from(this.cache.keys()),
      defaultTtl: this.defaultTtl,
    };
  }

  /**
   * Check if a specific cache entry exists and is valid
   */
  has(
    environment: string,
    timeframe: string,
    endpoint: string,
    additionalParams?: Record<string, any>,
  ): boolean {
    const key = this.generateKey(environment, timeframe, endpoint, additionalParams);
    const entry = this.cache.get(key);

    if (!entry) {
      return false;
    }

    // Check if entry has expired
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }
}

// Export a singleton instance with configuration optimized for API responses
export const cacheService = new CacheService({
  ttl: 5 * 60 * 1000, // 5 minutes default TTL
  maxSize: 200, // Allow up to 200 cached responses
});
